import { useEffect, useRef, useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import { useDistributionPreviewActive } from "./DistributionCard";
import { PreviewVideo } from "./PreviewVideo";

const START_DELAY = 60;
const STAGGER_DELAY = 250;
const PLAY_DURATION = 6000;
const LOAD_TIMEOUT = 5000;
const FADE_DURATION = 160;

type Phase = "preload" | "play" | "retiring";
interface Selection { id: number; card: HTMLElement; cover: HTMLElement; url: string; phase: Phase }
interface Slot { selection: Selection; ready: boolean; started: boolean; enteredViewport?: boolean; timer?: ReturnType<typeof setTimeout> }
interface Connection extends EventTarget { saveData?: boolean; effectiveType?: string }

/** Visible playback and a small lookahead buffer share the same video elements. */
export function ExploreAutoPreview({ container, paused }: { container: RefObject<HTMLDivElement | null>; paused: boolean }) {
	const manualPreview = useDistributionPreviewActive();
	const [selections, setSelections] = useState<Selection[]>([]);
	const lastPlayed = useRef(new WeakMap<HTMLElement, number>());
	const failed = useRef(new Set<string>());
	const sequence = useRef(0);
	const playback = useRef({ ready: (_id: number) => {}, playing: (_id: number) => {}, failed: (_id: number) => {} });

	useEffect(() => {
		const root = container.current;
		if (!root || paused || manualPreview || !("IntersectionObserver" in window)) return;
		const mobile = window.matchMedia("(max-width: 767px), (pointer: coarse)");
		const connection = (navigator as Navigator & { connection?: Connection }).connection;
		const visible = new Set<HTMLElement>();
		const nearby = new Set<HTMLElement>();
		const observed = new Set<HTMLElement>();
		const slots = new Map<number, Slot>();
		const scrollRoot = root.closest(".discover-content");
		let lastScrollTop = scrollRoot?.scrollTop ?? 0;
		let forward = true;
		let timer: ReturnType<typeof setTimeout> | undefined;
		let bufferTimer: ReturnType<typeof setTimeout> | undefined;
		let nextStart = 0;
		let scrollingUntil = 0;
		let visibilityKnown = false;
		let disposed = false;
		// Keep the page lively while still bounding decoder and network work.
		const limit = () => mobile.matches ? 2 : 5;
		// OS animation preferences affect UI transitions, not game preview playback.
		const allowed = () => !document.hidden && !connection?.saveData &&
			!["slow-2g", "2g"].includes(connection?.effectiveType ?? "");
		const active = () => [...slots.values()].filter(slot => slot.selection.phase !== "preload");
		const publish = () => setSelections([...slots.values()].map(slot => slot.selection));
		const remove = (id: number) => {
			const slot = slots.get(id);
			if (!slot) return;
			clearTimeout(slot.timer);
			slot.selection.cover.querySelector<HTMLVideoElement>(".explore-inline-video")?.pause();
			slots.delete(id);
			publish();
		};
		const clear = () => {
			clearTimeout(timer); timer = undefined;
			clearTimeout(bufferTimer); bufferTimer = undefined;
			for (const id of slots.keys()) remove(id);
		};
		// Start with ready covers; one slow image must not stall every visible game.
		const coverGate = () => visibilityKnown && [...visible].some(card => card.querySelector('[data-cover-state="loaded"]'));
		const shelf = (card: HTMLElement) => card.closest("[data-explore-layout]");
		function ranked(cards: HTMLElement[], includeBuffers = false) {
			const playing = (includeBuffers ? [...slots.values()] : active()).map(slot => slot.selection);
			const countInShelf = (card: HTMLElement) => playing.filter(selection => shelf(selection.card) === shelf(card)).length;
			const distance = (card: HTMLElement) => {
				const neighbors = playing.filter(selection => shelf(selection.card) === shelf(card));
				return neighbors.length ? Math.min(...neighbors.map(selection =>
					Math.abs(selection.card.getBoundingClientRect().left - card.getBoundingClientRect().left))) : 0;
			};
			return cards.sort((a, b) => countInShelf(a) - countInShelf(b) ||
				(lastPlayed.current.get(a) ?? 0) - (lastPlayed.current.get(b) ?? 0) || distance(b) - distance(a));
		}
		const schedule = (delay = START_DELAY) => {
			if (disposed || timer || !allowed() || active().length >= limit() || active().some(slot => !slot.started)) return;
			timer = setTimeout(choose, Math.max(delay, nextStart - Date.now(), scrollingUntil - Date.now()));
		};
		const scheduleBuffer = () => {
			if (disposed || bufferTimer || !allowed()) return;
			bufferTimer = setTimeout(prepare, 20);
		};
		function prepare() {
			bufferTimer = undefined;
			if (disposed || !allowed() || !coverGate()) return;
			const waiting = [...slots.values()].filter(slot => slot.selection.phase === "preload");
			// Two desktop requests keep a slow clip from blocking the queue. Mobile stays serial.
			if ([...slots.values()].filter(slot => !slot.ready).length >= (mobile.matches ? 1 : 2)) return;
			// Keep at most one extra desktop/mobile budget ready; never fetch the full catalogue.
			// A visible candidate can replace a stale offscreen buffer after a fast scroll.
			const all = [...root!.querySelectorAll<HTMLElement>(".distribution-card")];
			const candidates = all.filter(card => (visible.has(card) || nearby.has(card)) && card.dataset.previewVideo &&
				card.querySelector('[data-cover-state="loaded"]') &&
				!failed.current.has(card.dataset.previewVideo) && ![...slots.values()].some(slot => slot.selection.url === card.dataset.previewVideo));
			const onscreen = ranked(candidates.filter(card => visible.has(card)), true);
			const edge = scrollRoot?.getBoundingClientRect() ?? { top: 0, bottom: innerHeight };
			const aheadDistance = (card: HTMLElement) => {
				const rect = card.getBoundingClientRect();
				const offset = forward ? rect.top - edge.bottom : edge.top - rect.bottom;
				return offset >= 0 ? offset : Math.abs(offset) + innerHeight;
			};
			const offscreen = candidates.filter(card => !visible.has(card)).sort((a, b) => aheadDistance(a) - aheadDistance(b));
			// Reserve a ready buffer for the next viewport once the visible playback budget is filled.
			const lookAhead = active().length >= limit() && !waiting.some(slot => !visible.has(slot.selection.card));
			const cards = lookAhead ? [...offscreen, ...onscreen] : [...onscreen, ...offscreen];
			if (waiting.length >= limit()) {
				const stale = waiting.find(slot => !visible.has(slot.selection.card));
				if (lookAhead && offscreen.length) {
					// Scrolling may leave all buffers onscreen: keep one slot preparing the next shelf.
					remove(waiting[waiting.length - 1].selection.id);
				} else if (active().length < limit() && !waiting.some(slot => visible.has(slot.selection.card)) && stale && onscreen.length) {
					remove(stale.selection.id);
				} else return;
			}
			const card = cards[0];
			const cover = card?.querySelector<HTMLElement>(".distribution-cover");
			if (!card || !cover) return;
			const id = ++sequence.current;
			const selection: Selection = { id, card, cover, url: card.dataset.previewVideo!, phase: "preload" };
			slots.set(id, { selection, ready: false, started: false, timer: setTimeout(() => fail(id), LOAD_TIMEOUT) });
			publish(); scheduleBuffer();
		}
		function choose() {
			timer = undefined;
			if (disposed || !allowed() || !coverGate() || active().length >= limit()) return;
			const waiting = [...slots.values()].filter(slot => slot.selection.phase === "preload" && slot.ready &&
				visible.has(slot.selection.card) && slot.selection.card.querySelector('[data-cover-state="loaded"]'));
			const arriving = waiting.filter(slot => slot.enteredViewport);
			const card = ranked((arriving.length ? arriving : waiting).map(slot => slot.selection.card))[0];
			const slot = waiting.find(entry => entry.selection.card === card);
			if (!slot) { scheduleBuffer(); return; }
			slot.selection = { ...slot.selection, phase: "play" };
			slot.timer = setTimeout(() => fail(slot.selection.id), LOAD_TIMEOUT);
			lastPlayed.current.set(slot.selection.card, Date.now());
			publish(); scheduleBuffer();
		}
		function fail(id: number) {
			const slot = slots.get(id);
			if (!slot) return;
			failed.current.add(slot.selection.url);
			remove(id); schedule(); scheduleBuffer();
		}
		function retire(id: number) {
			const slot = slots.get(id);
			if (!slot) return;
			slot.selection = { ...slot.selection, phase: "retiring" };
			slot.selection.cover.querySelector<HTMLVideoElement>(".explore-inline-video")?.pause();
			publish();
			slot.timer = setTimeout(() => { remove(id); schedule(); scheduleBuffer(); }, FADE_DURATION);
		}
		playback.current = {
			ready(id) {
				const slot = slots.get(id);
				if (!slot || slot.ready) return;
				slot.ready = true;
				clearTimeout(slot.timer); slot.timer = undefined;
				schedule(); scheduleBuffer();
			},
			playing(id) {
				const slot = slots.get(id);
				if (!slot || slot.started || slot.selection.phase !== "play") return;
				slot.started = true;
				clearTimeout(slot.timer);
				slot.timer = setTimeout(() => retire(id), PLAY_DURATION);
				nextStart = Date.now() + STAGGER_DELAY;
				schedule(STAGGER_DELAY); scheduleBuffer();
			},
			failed: fail,
		};
		const observer = new IntersectionObserver(entries => {
			visibilityKnown = true;
			for (const entry of entries) {
				const card = entry.target as HTMLElement;
				if (entry.isIntersecting && entry.intersectionRatio >= .7) {
					if (!visible.has(card)) for (const slot of slots.values()) {
						if (slot.selection.card === card && slot.selection.phase === "preload" && slot.ready) slot.enteredViewport = true;
					}
					visible.add(card);
				}
				else visible.delete(card);
			}
			for (const slot of active()) if (!visible.has(slot.selection.card)) remove(slot.selection.id);
			schedule(); scheduleBuffer();
		}, { threshold: [0, .7] });
		const nearObserver = new IntersectionObserver(entries => {
			for (const entry of entries) {
				const card = entry.target as HTMLElement;
				if (entry.isIntersecting && entry.intersectionRatio >= .7) nearby.add(card); else nearby.delete(card);
			}
			for (const [id, slot] of slots) if (!nearby.has(slot.selection.card) && !visible.has(slot.selection.card)) remove(id);
			scheduleBuffer();
		}, { root: scrollRoot, rootMargin: "1000px 0px", threshold: [0, .7] });
		const sync = () => {
			for (const card of observed) if (!root.contains(card)) {
				observer.unobserve(card); nearObserver.unobserve(card); observed.delete(card); visible.delete(card); nearby.delete(card);
			}
			for (const card of root.querySelectorAll<HTMLElement>(".distribution-card")) if (!observed.has(card)) {
				observed.add(card); observer.observe(card); nearObserver.observe(card);
			}
			for (const [id, { selection }] of slots) if (!root.contains(selection.card) || selection.card.dataset.previewVideo !== selection.url) remove(id);
			schedule(); scheduleBuffer();
		};
		const mutations = new MutationObserver(sync);
		mutations.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ["data-cover-state", "data-preview-video"] });
		const restart = () => { clear(); nextStart = Date.now() + START_DELAY; schedule(); scheduleBuffer(); };
		const onScroll = (event: Event) => {
			const target = event.target;
			if (target === document || target instanceof Element && (target.contains(root) || root.contains(target))) {
				const top = scrollRoot?.scrollTop ?? 0;
				if (top !== lastScrollTop) forward = top > lastScrollTop;
				lastScrollTop = top;
				// Keep visible playback and downloaded buffers. Only new starts wait for a short pause.
				clearTimeout(timer); timer = undefined;
				scrollingUntil = Date.now() + START_DELAY;
				// The new viewport should not inherit the previous viewport's stagger delay.
				nextStart = 0;
				schedule(); scheduleBuffer();
			}
		};
		sync();
		window.addEventListener("scroll", onScroll, true);
		window.addEventListener("resize", restart);
		document.addEventListener("visibilitychange", restart);
		mobile.addEventListener("change", restart);
		connection?.addEventListener("change", restart);
		return () => {
			disposed = true;
			clear(); observer.disconnect(); nearObserver.disconnect(); mutations.disconnect();
			playback.current = { ready: () => {}, playing: () => {}, failed: () => {} };
			window.removeEventListener("scroll", onScroll, true);
			window.removeEventListener("resize", restart);
			document.removeEventListener("visibilitychange", restart);
			mobile.removeEventListener("change", restart);
			connection?.removeEventListener("change", restart);
		};
	}, [container, paused, manualPreview]);

	return !paused && !manualPreview ? selections.map(selection => createPortal(
		<InlineVideo selection={selection} onReady={() => playback.current.ready(selection.id)}
			onPlaying={() => playback.current.playing(selection.id)} onFailed={() => playback.current.failed(selection.id)} />,
		selection.cover, String(selection.id),
	)) : null;
}

function InlineVideo({ selection, onReady, onPlaying, onFailed }: {
	selection: Selection; onReady: () => void; onPlaying: () => void; onFailed: () => void;
}) {
	return <PreviewVideo url={selection.url} play={selection.phase === "play"} priority={1}
		className={"distribution-preview-video explore-inline-video " +
		(selection.phase === "preload" ? "explore-preload-video" : "explore-auto-preview-video")}
		onReady={onReady} onPlaying={onPlaying} onFailed={onFailed} />;
}
